import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/** Pure rules for the Bill automation console. No React here, so every number the console shows is testable. */
import { CREDENTIAL_STATUS, credentialStatusLabel } from 'src/components/property/UtilityPortalCredentials/credentialStatus';
export const STALE_AFTER_HOURS = 36;

/**
 * Full unless the document says partial. Health documents before schema 3 carry no scope: they count as full
 * when they swept at least 75% of the registry's scheduled providers (on 103 prod documents that rule marked
 * every daily sweep full and every canary partial).
 */
export const isFullSweep = doc => {
  var _doc$totals;
  if (doc !== null && doc !== void 0 && doc.scope) return doc.scope === 'full';
  const scheduled = ((doc === null || doc === void 0 ? void 0 : doc.registry) || []).filter(r => r.scheduled).length;
  return scheduled > 0 && ((doc === null || doc === void 0 || (_doc$totals = doc.totals) === null || _doc$totals === void 0 ? void 0 : _doc$totals.providers) || 0) >= 0.75 * scheduled;
};
export const fullSweeps = docs => (docs || []).filter(isFullSweep);
/** Documents arrive newest first. */
export const latestFullSweep = docs => fullSweeps(docs)[0] || null;
export const ageHours = function (iso) {
  let now = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Date.now();
  return iso ? (now - Date.parse(iso)) / 36e5 : Infinity;
};
export const isStale = (iso, now) => ageHours(iso, now) > STALE_AFTER_HOURS;
export const formatAge = (iso, now) => {
  const h = ageHours(iso, now);
  if (!Number.isFinite(h)) return 'never';
  if (h < 1) return 'under an hour ago';
  return h < 48 ? "".concat(Math.floor(h), " h ago") : "".concat(Math.floor(h / 24), " days ago");
};

/** How a failure class reads to an operator. `configuration_error` is the one that matters most:
 *  it is OUR missing config, not a portal defending itself, and it used to hide as captcha_blocked. */
export const FAILURE_CLASS_LABELS = {
  configuration_error: 'Our config is missing',
  credential_failure: 'Bad credentials',
  mfa_required: 'MFA wall',
  captcha_blocked: 'Captcha blocked',
  infrastructure_failure: 'Network / proxy',
  fetcher_failure: 'Fetcher failed',
  ambiguous: 'Unclassified'
};
const NEEDS_PERSON = new Set(['credential_failure', 'mfa_required', 'configuration_error']);
const RETRYING = new Set(['captcha_blocked', 'infrastructure_failure', 'fetcher_failure']);
const needsPerson = p => p.action_required || NEEDS_PERSON.has(p.failure_class);

/**
 * One label per provider. A run that tried and was told a person must act wins over its own status; a run that
 * did not try (skipped, skipped-all) keeps its status whatever failure_class it carries.
 */
export const providerStatus = p => {
  switch (p === null || p === void 0 ? void 0 : p.status) {
    case undefined:
    case null:
      return 'never';
    case 'ok':
      return 'ok';
    case 'skipped-all':
      return 'not_due';
    case 'skipped':
      return 'no_accounts';
    case 'kept-none':
      return needsPerson(p) ? 'needs_person' : 'kept_none';
    case 'error':
      return needsPerson(p) ? 'needs_person' : 'error';
    case 'no-bills':
      if (needsPerson(p)) return 'needs_person';
      if (RETRYING.has(p.failure_class)) return 'retrying';
      return p.failure_class === 'ambiguous' ? 'inconclusive' : 'no_new';
    default:
      return 'inconclusive';
  }
};

/** [label, tone]. Tone is a Chip variant slot in src/styles/components/chip.ts. */
export const STATUS_META = {
  ok: ['Bills found', 'success'],
  kept_none: ['All quarantined', 'warning'],
  no_new: ['Nothing new', 'inactive'],
  not_due: ['Not due', 'inactive'],
  no_accounts: ['No mapped accounts', 'inactive'],
  inconclusive: ['Inconclusive', 'warning'],
  needs_person: ['Needs a person', 'critical'],
  retrying: ['Failed, retrying', 'warning'],
  error: ['Error', 'critical'],
  never: ['Never ran', 'inactive']
};
const STATUS_ORDER = ['needs_person', 'error', 'retrying', 'kept_none', 'inconclusive', 'ok', 'no_new', 'not_due', 'no_accounts', 'never'];
export const byStatusThenVolume = (a, b) => STATUS_ORDER.indexOf(providerStatus(a)) - STATUS_ORDER.indexOf(providerStatus(b)) || (b.downloaded || 0) - (a.downloaded || 0);
/** Failure class reads as a problem only when the run actually tried and failed. */
export const showsProblem = p => ['needs_person', 'retrying', 'inconclusive', 'error'].includes(providerStatus(p));

/** Sum over full sweeps only, so a canary never inflates the fleet. */
export const fleetTotals = docs => fullSweeps(docs).reduce((a, d) => {
  var _d$totals, _d$totals2;
  return {
    sweeps: a.sweeps + 1,
    pdfs: a.pdfs + (((_d$totals = d.totals) === null || _d$totals === void 0 ? void 0 : _d$totals.pdfs) || 0),
    stamped: a.stamped + (((_d$totals2 = d.totals) === null || _d$totals2 === void 0 ? void 0 : _d$totals2.stamped) || 0)
  };
}, {
  sweeps: 0,
  pdfs: 0,
  stamped: 0
});

/**
 * Two funnels, because they count different things over different windows. Logins count property utilities now
 * (a share of those mapped); bills count this month's scraped bills (a share of the team's monthly bills). A drop
 * is only ever measured inside one funnel.
 */
export const FUNNELS = {
  logins: {
    title: 'Logins, in property utilities',
    base: doc => {
      var _doc$levels;
      return ((_doc$levels = doc.levels) === null || _doc$levels === void 0 ? void 0 : _doc$levels.l1_mapped) || 0;
    },
    stages: [['l1_mapped', 'Mapped to a login', 'levels'], ['l2_login_works', 'Login has worked', 'levels'], ['l3_retrieved', 'Bill retrieved today', 'levels']]
  },
  bills: {
    title: 'Scraped bills this month',
    base: doc => {
      var _doc$baseline;
      return ((_doc$baseline = doc.baseline) === null || _doc$baseline === void 0 ? void 0 : _doc$baseline.team_bills_cycle) || 0;
    },
    stages: [['posted_mtd', 'Posted to the app', 'mtd'], ['verified_mtd', 'Verified', 'mtd'], ['auto_allocated_mtd', 'Auto-allocated', 'mtd'], ['fully_automated_mtd', 'Fully automated', 'mtd']]
  }
};
export const funnel = (doc, which) => {
  if (!doc) return [];
  const spec = FUNNELS[which];
  const base = spec.base(doc);
  const rows = spec.stages.map(_ref => {
    var _doc$from;
    let _ref2 = _slicedToArray(_ref, 3),
      key = _ref2[0],
      label = _ref2[1],
      from = _ref2[2];
    return {
      key,
      label,
      value: ((_doc$from = doc[from]) === null || _doc$from === void 0 ? void 0 : _doc$from[key]) || 0
    };
  });
  return rows.map((r, i) => _objectSpread(_objectSpread({}, r), {}, {
    share: base ? Math.min(100, Math.round(r.value / base * 100)) : 0,
    drop: i ? Math.max(0, rows[i - 1].value - r.value) : 0
  }));
};
export const worstDrop = stages => stages.reduce((w, s) => s.drop > ((w === null || w === void 0 ? void 0 : w.drop) || 0) ? s : w, null);
export const headline = doc => {
  var _doc$mtd, _doc$baseline2;
  if (!doc) return null;
  const verified = ((_doc$mtd = doc.mtd) === null || _doc$mtd === void 0 ? void 0 : _doc$mtd.verified_mtd) || 0;
  const baseline = ((_doc$baseline2 = doc.baseline) === null || _doc$baseline2 === void 0 ? void 0 : _doc$baseline2.team_bills_cycle) || 0;
  return {
    verified,
    baseline,
    pct: baseline ? Math.round(verified / baseline * 1000) / 10 : 0
  };
};

/**
 * One point per daily document of the newest document's month, oldest first. Month-to-date counts reset on the
 * 1st, so earlier months would read as a cliff. All three series count bills, so they share one axis.
 */
export const trend = docs => {
  var _docs$;
  const month = String((docs === null || docs === void 0 || (_docs$ = docs[0]) === null || _docs$ === void 0 ? void 0 : _docs$.date) || '').slice(0, 7);
  return [...(docs || [])].filter(d => String(d.date || '').startsWith(month)).reverse().map(d => {
    var _d$mtd, _d$mtd2, _d$mtd3;
    return {
      date: String(d.date || '').slice(5),
      posted: ((_d$mtd = d.mtd) === null || _d$mtd === void 0 ? void 0 : _d$mtd.posted_mtd) || 0,
      verified: ((_d$mtd2 = d.mtd) === null || _d$mtd2 === void 0 ? void 0 : _d$mtd2.verified_mtd) || 0,
      automated: ((_d$mtd3 = d.mtd) === null || _d$mtd3 === void 0 ? void 0 : _d$mtd3.fully_automated_mtd) || 0
    };
  });
};

/** Login buckets are the credentials page's own labels, so tiles, chips and the scoreboard agree. */
export const LOGIN_BUCKETS = {
  active: CREDENTIAL_STATUS.ACTIVE,
  unverified: CREDENTIAL_STATUS.UNVERIFIED,
  needs_attention: CREDENTIAL_STATUS.NEEDS_ATTENTION,
  paused: CREDENTIAL_STATUS.PAUSED,
  disabled: CREDENTIAL_STATUS.DISABLED
};
const BUCKET_BY_LABEL = Object.fromEntries(Object.entries(LOGIN_BUCKETS).map(_ref3 => {
  let _ref4 = _slicedToArray(_ref3, 2),
    k = _ref4[0],
    v = _ref4[1];
  return [v, k];
}));
export const loginBucket = credential => BUCKET_BY_LABEL[credentialStatusLabel(credential)];
export const loginSummary = credentials => (credentials || []).reduce((a, c) => _objectSpread(_objectSpread({}, a), {}, {
  [loginBucket(c)]: a[loginBucket(c)] + 1,
  total: a.total + 1
}), {
  total: 0,
  active: 0,
  unverified: 0,
  needs_attention: 0,
  paused: 0,
  disabled: 0
});

/** Today's work, in a fixed order (failures before backlog). Only kinds with something to do. */
export const inbox = _ref5 => {
  var _sweep$totals;
  let credentials = _ref5.credentials,
    sweep = _ref5.sweep,
    scoreboard = _ref5.scoreboard;
  const logins = loginSummary(credentials);
  const held = ((scoreboard === null || scoreboard === void 0 ? void 0 : scoreboard.held) || []).filter(h => h.held_reason).reduce((n, h) => n + h.n, 0);
  return [{
    kind: 'login_needs_attention',
    n: logins.needs_attention,
    view: 'logins',
    filter: 'needs_attention'
  }, {
    kind: 'login_paused',
    n: logins.paused,
    view: 'logins',
    filter: 'paused'
  }, {
    kind: 'provider_needs_person',
    n: ((sweep === null || sweep === void 0 ? void 0 : sweep.providers) || []).filter(p => providerStatus(p) === 'needs_person').length,
    view: 'providers',
    filter: 'needs_person'
  }, {
    kind: 'bills_held',
    n: held,
    href: '/bill-queue'
  }, {
    kind: 'quarantined',
    n: (sweep === null || sweep === void 0 || (_sweep$totals = sweep.totals) === null || _sweep$totals === void 0 ? void 0 : _sweep$totals.quarantined) || 0,
    view: 'sweeps'
  }, {
    kind: 'wall_retry_due',
    n: ((sweep === null || sweep === void 0 ? void 0 : sweep.registry) || []).filter(r => r.walled && r.retry_due).length,
    view: 'providers',
    filter: 'walled'
  }, {
    kind: 'login_unverified',
    n: logins.unverified,
    view: 'logins',
    filter: 'unverified'
  }].filter(i => i.n > 0);
};
const counted = (n, one, many) => "".concat(n.toLocaleString(), " ").concat(n === 1 ? one : many);
export const INBOX_COPY = {
  login_needs_attention: n => "".concat(counted(n, 'login needs', 'logins need'), " attention"),
  login_paused: n => "".concat(counted(n, 'login is', 'logins are'), " paused after 3 failed sign-ins"),
  provider_needs_person: n => "".concat(counted(n, 'provider needs', 'providers need'), " a person after the last sweep"),
  bills_held: n => "".concat(counted(n, 'scraped bill is', 'scraped bills are'), " held for an account check"),
  quarantined: n => "".concat(counted(n, 'bill was', 'bills were'), " quarantined in the last sweep"),
  wall_retry_due: n => "".concat(counted(n, 'walled provider is', 'walled providers are'), " due for a retry"),
  login_unverified: n => "".concat(counted(n, 'login has', 'logins have'), " not been verified yet")
};
/** The Overview tab badge: things that broke, not backlog. */
export const urgentCount = items => items.filter(i => ['login_needs_attention', 'login_paused', 'provider_needs_person'].includes(i.kind)).reduce((n, i) => n + i.n, 0);

/**
 * The Providers view filters over coverage rows joined to the last full sweep (`statusKey`) and the scoreboard
 * (`logins`). needs_person uses the inbox's rule so the count on the inbox button is the length of this list.
 */
export const matchesProviderFilter = (row, filter) => {
  var _all$needs_person$sup, _row$logins, _row$logins2;
  return (_all$needs_person$sup = {
    all: true,
    // Same rule the Overview inbox counts with, so the number on the button is the length of this list.
    needs_person: row.statusKey === 'needs_person',
    supported: row.automation === 'Automated' || row.automation === 'ScraperIdle',
    // A supported provider with name-joined rows still has utilities fetched by hand; it belongs on both lists.
    unsupported: row.automation === 'Manual' || row.unmappedUtilities > 0,
    manual: row.automation === 'Manual' || row.unmappedUtilities > 0,
    unused: row.automation === 'Dormant',
    walled: row.walled,
    // Live coverage counts too: the scoreboard is daily and entitlement-scoped.
    // actionRequired is the coverage API's own flag (bad login, MFA wall or missing config), possibly from a later
    // canary than the last full sweep, so it lives here rather than in needs_person, which must match the inbox.
    failing: row.actionRequired || (((_row$logins = row.logins) === null || _row$logins === void 0 ? void 0 : _row$logins.needs_attention) || 0) + (((_row$logins2 = row.logins) === null || _row$logins2 === void 0 ? void 0 : _row$logins2.paused) || 0) + (row.credentialsNeedingAttention || 0) > 0
  }[filter]) !== null && _all$needs_person$sup !== void 0 ? _all$needs_person$sup : true;
};

/** Downloaded bills per full sweep for one provider, oldest first; empty when no sweep ever ran it. */
export const providerSeries = function (docs, key) {
  let n = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 14;
  const ran = fullSweeps(docs).slice(0, n).reverse().map(d => (d.providers || []).find(p => p.key === key));
  return ran.some(Boolean) ? ran.map(p => (p === null || p === void 0 ? void 0 : p.downloaded) || 0) : [];
};

/** This environment's logins per provider key, in the same buckets as the Logins view it links to. */
export const loginsByProvider = credentials => {
  const groups = new Map();
  (credentials || []).forEach(c => {
    const key = (c.providerKey || '').toLowerCase();
    groups.set(key, [...(groups.get(key) || []), c]);
  });
  return new Map([...groups].map(_ref6 => {
    let _ref7 = _slicedToArray(_ref6, 2),
      key = _ref7[0],
      rows = _ref7[1];
    return [key, _objectSpread(_objectSpread({}, loginSummary(rows)), {}, {
      logins: rows.length
    })];
  }));
};
const sumLogins = (logins, keys) => {
  const parts = keys.map(k => logins.get(k)).filter(Boolean);
  return parts.length ? parts.reduce((a, b) => Object.fromEntries(Object.keys(a).map(f => [f, (a[f] || 0) + (b[f] || 0)]))) : undefined;
};

// Canonical names getUtility() knows (lower-cased exact match in @livable/saas-react-components 3.3.0).
const UTILITY_KIND_RULES = [['Electricity', /electric|lighting|\bpower\b|\bsolar\b/], ['Natural gas', /\bgas\b|propane/], ['Water', /water|irrigation/], ['Sewer', /sewer|storm/], ['Trash', /trash|garbage|refuse|sanitation|waste|recycl|compost|bulky/], ['Internet', /internet|cable|wi-?fi|fiber|telecom|phone|spectrum|\btv\b/]];

/** Free-text utility type names to the icon kinds they bill, in a fixed order; Other when nothing matches. */
export const utilityKinds = function () {
  let names = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  const found = new Set();
  names.forEach(name => {
    // "Gas for Hot Water" is a gas bill; wastewater is sewer, not water plus trash.
    const text = (name || '').toLowerCase().replace(/hot water/g, '').replace(/waste\s*water/g, 'sewer');
    const hits = UTILITY_KIND_RULES.filter(_ref8 => {
      let _ref9 = _slicedToArray(_ref8, 2),
        rx = _ref9[1];
      return rx.test(text);
    }).map(_ref0 => {
      let _ref1 = _slicedToArray(_ref0, 1),
        kind = _ref1[0];
      return kind;
    });
    (hits.length ? hits : ['Other']).forEach(kind => found.add(kind));
  });
  return [...UTILITY_KIND_RULES.map(_ref10 => {
    let _ref11 = _slicedToArray(_ref10, 1),
      kind = _ref11[0];
    return kind;
  }), 'Other'].filter(kind => found.has(kind));
};

/**
 * Coverage rows (one per Livable provider, plus one per registry key no provider uses, so every swept key has a row)
 * joined to the last full sweep, its registry entry, this environment's logins, the scoreboard's month-to-date bills
 * and a trend. Logins come from this environment so the row agrees with the Logins view it opens.
 */
export const joinProviders = (coverageRows, healthDocs, scoreboardDoc, credentials) => {
  const sweep = latestFullSweep(healthDocs);
  const ran = new Map(((sweep === null || sweep === void 0 ? void 0 : sweep.providers) || []).map(p => [p.key, p]));
  const registry = new Map(((sweep === null || sweep === void 0 ? void 0 : sweep.registry) || []).map(r => [r.key, r]));
  const bills = new Map(((scoreboardDoc === null || scoreboardDoc === void 0 ? void 0 : scoreboardDoc.by_provider) || []).map(p => [p.provider_key, p]));
  const logins = loginsByProvider(credentials);
  return (coverageRows || []).map(row => {
    var _row$loginKeys;
    const key = row.scraperProviderKey;
    // Logins on a provider no scraper covers are keyed provider-<UtilityProvider id, no dashes> in property-service.
    const loginKey = (key || (row.utilityProviderId ? "provider-".concat(row.utilityProviderId.replace(/-/g, '')) : '')).toLowerCase() || null;
    const last = ran.get(key);
    // A family's logins sit under every member's key; an older API sends none, so fall back to the one key.
    const loginKeys = ((_row$loginKeys = row.loginKeys) !== null && _row$loginKeys !== void 0 && _row$loginKeys.length ? row.loginKeys : [loginKey].filter(Boolean)).map(k => k.toLowerCase());
    return _objectSpread(_objectSpread({}, row), {}, {
      last,
      reg: registry.get(key),
      loginKey,
      loginKeys,
      logins: sumLogins(logins, loginKeys),
      bills: bills.get(key),
      statusKey: last ? providerStatus(last) : 'never',
      series: key ? providerSeries(healthDocs, key) : []
    });
  });
};